import { defineStore } from 'pinia';
import http from '@/api/http';
import { redirectToLogin } from '@/utils/authRedirect';
import { logPerf, perfNow } from '@/utils/perf';
import { adminHealth } from '@/api/admin';
import { logAuthDebug, warnAuthDebug } from '@/utils/authDebug';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('ml_token') || null,
    authStatus: localStorage.getItem('ml_token') ? 'unknown' : 'anonymous',
    authResolved: false,
    authInitializing: false,
    loading: false,
    checkingMe: false,
    error: null,
    registerSuccess: null,
    trialStatus: null,
    pendingVerificationEmail: null,
    adminAccess: null,
    sessionRestoreAttempted: false,
    restoringSession: false,
    loginPromise: null,
    logoutPromise: null,
    restorePromise: null,
    mePromise: null,
    initPromise: null,
    lastAuthHydratedAt: 0,
    authOperationSeq: 0,
    activeAuthOperationId: 0,
    activeAuthOperationType: null,
    authStateVersion: 0,
    loginInProgress: false,
    restoreAbortController: null
  }),
  actions: {
    beginAuthOperation(type, options = {}) {
      const { bumpStateVersion = false } = options;
      const operationId = this.authOperationSeq + 1;
      this.authOperationSeq = operationId;
      this.activeAuthOperationId = operationId;
      this.activeAuthOperationType = type;
      if (bumpStateVersion) {
        this.authStateVersion += 1;
      }
      return {
        operationId,
        authStateVersion: this.authStateVersion
      };
    },
    isAuthOperationCurrent(operationId) {
      return this.activeAuthOperationId === operationId;
    },
    canWriteAuthState({ operationId, authStateVersion }) {
      return this.authStateVersion === authStateVersion;
    },
    applyAnonymousState() {
      this.user = null;
      this.token = null;
      this.trialStatus = null;
      this.adminAccess = null;
      this.authStatus = 'anonymous';
      localStorage.removeItem('ml_token');
      localStorage.removeItem('ml_refresh_token');
    },
    applyAuthenticatedState(payload = {}) {
      const { token, user, trialStatus } = payload;
      this.token = token || null;
      this.user = user || null;
      this.trialStatus = trialStatus || null;
      this.adminAccess = null;
      this.authStatus = this.token && this.user ? 'authenticated' : 'unknown';
      this.lastAuthHydratedAt = Date.now();
      if (this.token) {
        localStorage.setItem('ml_token', this.token);
      } else {
        localStorage.removeItem('ml_token');
      }
    },
    async initAuth(options = {}) {
      const { background = false, force = false } = options;

      if (this.authResolved && !force) {
        return this.authStatus;
      }
      if (this.initPromise) {
        return this.initPromise;
      }

      this.authInitializing = true;
      const authOp = this.beginAuthOperation('init');
      logAuthDebug('init.start', {
        background,
        authResolved: this.authResolved,
        authStatus: this.authStatus,
        hasToken: Boolean(this.token),
        hasUser: Boolean(this.user)
      });

      this.initPromise = (async () => {
        try {
          if (!this.canWriteAuthState(authOp)) {
            return this.authStatus;
          }
          if (this.token && !this.user) {
            await this.loadMe({ allowRedirect: false });
          } else if (!this.token) {
            await this.restoreSession({ background });
            if (this.token && !this.user) {
              await this.loadMe({ allowRedirect: false });
            }
          }

          if (!this.canWriteAuthState(authOp)) {
            return this.authStatus;
          }
          if (this.token && this.user) {
            this.authStatus = 'authenticated';
          } else if (!this.token) {
            this.authStatus = 'anonymous';
          } else {
            this.authStatus = 'unknown';
          }
          this.authResolved = true;
          logAuthDebug('init.success', {
            authStatus: this.authStatus,
            hasToken: Boolean(this.token),
            hasUser: Boolean(this.user)
          });
          return this.authStatus;
        } catch (error) {
          warnAuthDebug('init.fail', { message: error?.message || 'unknown_error' });
          if (this.canWriteAuthState(authOp) && !this.token) {
            this.applyAnonymousState();
          }
          this.authResolved = true;
          return this.authStatus;
        } finally {
          this.authInitializing = false;
          this.initPromise = null;
          logAuthDebug('init.end', { authStatus: this.authStatus });
        }
      })();

      return this.initPromise;
    },
    async register(payload) {
      this.loading = true;
      this.error = null;
      this.registerSuccess = null;
      try {
        const { data } = await http.post('/auth/register', payload);
        this.token = null;
        this.user = null;
        this.trialStatus = null;
        this.adminAccess = null;
        localStorage.removeItem('ml_token');
        localStorage.removeItem('ml_refresh_token');
        this.registerSuccess = data.message || 'Registrierung erfolgreich.';
      } catch (err) {
        this.error = err.response?.data?.message || 'Registrierung fehlgeschlagen.';
      } finally {
        this.loading = false;
      }
    },
    async login(payload) {
      if (this.loginPromise) {
        return this.loginPromise;
      }

      const authOp = this.beginAuthOperation('login', { bumpStateVersion: true });
      logAuthDebug('login.submit', { hasEmail: Boolean(payload?.email) });
      this.loading = true;
      this.error = null;
      this.pendingVerificationEmail = null;
      this.adminAccess = null;
      this.loginInProgress = true;
      if (this.restoreAbortController) {
        this.restoreAbortController.abort();
      }
      this.loginPromise = (async () => {
        try {
          logAuthDebug('login.request');
          const { data } = await http.post('/auth/login', payload, {
            timeout: 30000
          });
          if (!this.canWriteAuthState(authOp)) {
            return;
          }
          this.applyAuthenticatedState({
            token: data?.token,
            user: data?.user || null,
            trialStatus: data?.trialStatus || null
          });
          if (!this.user) {
            await this.loadMe({ allowRedirect: false, force: true });
          }
          if (!this.canWriteAuthState(authOp)) {
            return;
          }
          this.authStatus = 'authenticated';
          this.authResolved = true;
          this.sessionRestoreAttempted = true;
          localStorage.removeItem('ml_refresh_token');
          logAuthDebug('login.success', { authStatus: this.authStatus });
        } catch (err) {
          if (!this.canWriteAuthState(authOp)) {
            return;
          }
          const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
          this.error = isTimeout
            ? 'Login-Zeitüberschreitung. Bitte prüfe deine Verbindung und versuche es erneut.'
            : (err.response?.data?.message || 'Login fehlgeschlagen.');
          warnAuthDebug('login.fail', { status: err?.response?.status, code: err?.response?.data?.code });
          if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
            this.pendingVerificationEmail = payload?.email || null;
          }
        } finally {
          this.loading = false;
          this.loginInProgress = false;
          this.loginPromise = null;
        }
      })();

      return this.loginPromise;
    },
    async restoreSession(options = {}) {
      const { background = false } = options;

      if (this.token) {
        this.sessionRestoreAttempted = true;
        return this.token;
      }
      if (this.restorePromise) {
        return this.restorePromise;
      }
      if (this.sessionRestoreAttempted && !background) {
        return this.token;
      }

      this.restoringSession = true;
      const authOp = this.beginAuthOperation('restore');
      const restoreAbortController = new AbortController();
      this.restoreAbortController = restoreAbortController;
      logAuthDebug('restore.start', { background });
      this.restorePromise = (async () => {
        try {
          const { data } = await http.post('/auth/refresh', {}, {
            skipAuthRedirect: true,
            timeout: 15000,
            signal: restoreAbortController.signal
          });
          if (!this.canWriteAuthState(authOp)) {
            return this.token;
          }
          if (data?.token) {
            this.applyAuthenticatedState(data);
            logAuthDebug('restore.success', { hasUser: Boolean(this.user) });
          }
          return this.token;
        } catch (error) {
          if (error?.code === 'ERR_CANCELED' || restoreAbortController.signal.aborted) {
            logAuthDebug('restore.aborted');
            return this.token;
          }
          if (!this.canWriteAuthState(authOp)) {
            return this.token;
          }
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            if (!this.token && this.authStatus !== 'authenticated') {
              this.applyAnonymousState();
            }
            logAuthDebug('restore.unauthorized', { status });
            return null;
          }

          if (!this.token && this.authStatus !== 'authenticated') {
            this.applyAnonymousState();
          }
          warnAuthDebug('restore.fail', { status, message: error?.message || 'unknown_error' });
          return null;
        } finally {
          this.sessionRestoreAttempted = true;
          this.restoringSession = false;
          this.restorePromise = null;
          this.restoreAbortController = null;
        }
      })();

      return this.restorePromise;
    },
    async loadMe(options = {}) {
      const { allowRedirect = true, force = false } = options;
      if (!this.token) return null;
      if (this.mePromise) return this.mePromise;
      if (!force && this.user) return this.user;

      this.checkingMe = true;
      logAuthDebug('loadMe.start', { force, allowRedirect });
      const start = perfNow();
      this.mePromise = (async () => {
        try {
          const { data } = await http.get('/auth/me');
          if (this.loginInProgress) {
            return this.user;
          }
          this.applyAuthenticatedState({
            token: this.token,
            user: data.user,
            trialStatus: data.trialStatus
          });
          this.authStatus = 'authenticated';
          this.authResolved = true;
          logAuthDebug('loadMe.success');
          return this.user;
        } catch (err) {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            if (this.loginInProgress) {
              warnAuthDebug('loadMe.skip.anonymous', { reason: 'login_in_progress' });
              return this.user;
            }
            this.applyAnonymousState();
            if (allowRedirect) {
              redirectToLogin({ reason: 'expired' });
            }
            warnAuthDebug('loadMe.unauthorized', { status, allowRedirect });
          } else {
            console.error('Profil konnte nicht geladen werden.', err);
            this.user = null;
            warnAuthDebug('loadMe.fail', { status, message: err?.message || 'unknown_error' });
          }
          return this.user;
        } finally {
          logPerf('AuthCheck', perfNow() - start);
          this.checkingMe = false;
          this.mePromise = null;
        }
      })();

      return this.mePromise;
    },

    async ensureAuthenticatedUser() {
      await this.restoreSession();
      if (!this.token) return null;
      return this.loadMe();
    },

    async checkAdminAccess() {
      const isAdminUser = this.user?.isAdmin === true || this.user?.role === 'admin';
      if (!this.token || !isAdminUser) {
        this.adminAccess = false;
        return false;
      }

      try {
        await adminHealth({ skipAuthRedirect: true });
        this.adminAccess = true;
        return true;
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          this.adminAccess = false;
          return false;
        }
        throw e;
      }
    },
    async refreshMe() {
      const { data } = await http.get('/auth/me');
      if (data?.user) this.user = data.user;
      if (data?.trialStatus) this.trialStatus = data.trialStatus;
      return data;
    },
    async updateDashboardConfig(dashboardConfig) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await http.patch('/auth/me/dashboard', { dashboardConfig });
        this.user = data.user;
        this.trialStatus = data.trialStatus || this.trialStatus;
        return data;
      } catch (e) {
        this.error = e?.response?.data?.message || 'Konnte Dashboard nicht speichern.';
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async resendVerification(email) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await http.post('/auth/resend-verification', { email });
        this.registerSuccess = data.message || 'Verifizierungs-Mail wurde gesendet.';
      } catch (e) {
        this.error = e?.response?.data?.message || 'Verifizierungs-Mail konnte nicht gesendet werden.';
      } finally {
        this.loading = false;
      }
    },

    logout() {
      if (this.logoutPromise) {
        return this.logoutPromise;
      }
      this.beginAuthOperation('logout', { bumpStateVersion: true });

      const tokenBeforeReset = this.token;
      logAuthDebug('logout.click', { hasToken: Boolean(tokenBeforeReset) });
      this.pendingVerificationEmail = null;
      this.registerSuccess = null;
      this.sessionRestoreAttempted = true;
      this.restorePromise = null;
      if (this.restoreAbortController) {
        this.restoreAbortController.abort();
      }
      this.mePromise = null;
      this.loginPromise = null;
      this.loginInProgress = false;
      this.authResolved = true;
      this.applyAnonymousState();
      this.loading = false;

      this.logoutPromise = (async () => {
        try {
          if (tokenBeforeReset) {
            await http.post('/auth/logout', {}, {
              skipAuthRedirect: true,
              headers: {
                Authorization: `Bearer ${tokenBeforeReset}`
              }
            });
          }
          logAuthDebug('logout.success');
        } catch (error) {
          warnAuthDebug('logout.fail', { status: error?.response?.status, message: error?.message || 'unknown_error' });
        } finally {
          this.logoutPromise = null;
          logAuthDebug('logout.end');
        }
      })();

      return this.logoutPromise;
    }
  }
});
